import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useMembers } from '../../hooks/queries/useMembers';
import { isSuperUser } from '../../utils/permissions';
import {
  subscribeToUserPendingScorecards,
  confirmLiveScorecardHand,
  isScorecardPendingVerification,
  PendingScorecardSummary
} from '../../services/liveScorecardSyncService';
import {
  ChevronRight,
  ChevronLeft,
  X,
  Eye,
  Check,
  Shield,
  Save,
  Clock
} from 'lucide-react';
import toast from 'react-hot-toast';
import { AudioSynthHelper } from '../../arena/engine/audio/AudioSynthHelper';

let sharedScorecardAudioCtx: AudioContext | null = null;

function getSharedAudioContext(): AudioContext | null {
  try {
    if (typeof window === 'undefined') return null;
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioCtx) return null;

    if (!sharedScorecardAudioCtx || sharedScorecardAudioCtx.state === 'closed') {
      sharedScorecardAudioCtx = new AudioCtx();
    }
    const ctx = sharedScorecardAudioCtx;
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    return ctx;
  } catch {
    return null;
  }
}

export function playScorecardAddedSound(): void {
  try {
    const ctx = getSharedAudioContext();
    if (!ctx) return;

    AudioSynthHelper.playNoteSequence(ctx, [
      { freq: 523.25, start: 0.00, dur: 0.14, gain: 0.34, type: 'triangle' }, // C5
      { freq: 659.25, start: 0.08, dur: 0.16, gain: 0.38, type: 'triangle' }, // E5
      { freq: 783.99, start: 0.16, dur: 0.20, gain: 0.42, type: 'sine' },     // G5
      { freq: 1046.50, start: 0.25, dur: 0.38, gain: 0.46, type: 'sine' }     // C6
    ]);
  } catch {
    // Ignore audio context restrictions on un-interacted pages
  }
}

export function playScorecardPendingVerificationSound(): void {
  try {
    const ctx = getSharedAudioContext();
    if (!ctx) return;

    // Attention chime pair (F5 -> A5, then A5 -> D6)
    AudioSynthHelper.playNoteSequence(ctx, [
      { freq: 698.46, start: 0.00, dur: 0.12, gain: 0.35, type: 'triangle' }, // F5
      { freq: 880.00, start: 0.10, dur: 0.18, gain: 0.40, type: 'sine' },     // A5
      { freq: 880.00, start: 0.26, dur: 0.12, gain: 0.35, type: 'triangle' }, // A5
      { freq: 1174.66, start: 0.36, dur: 0.36, gain: 0.45, type: 'sine' }     // D6
    ]);
  } catch {
    // Ignore audio context restrictions
  }
}

export function playScorecardWaitingSubmissionSound(): void {
  try {
    const ctx = getSharedAudioContext();
    if (!ctx) return;

    // Triumphant rising flourish (G5 -> C6 -> E6 -> G6)
    AudioSynthHelper.playNoteSequence(ctx, [
      { freq: 783.99, start: 0.00, dur: 0.12, gain: 0.35, type: 'sine' },     // G5
      { freq: 1046.50, start: 0.09, dur: 0.14, gain: 0.40, type: 'triangle' }, // C6
      { freq: 1318.51, start: 0.18, dur: 0.18, gain: 0.42, type: 'triangle' }, // E6
      { freq: 1567.98, start: 0.28, dur: 0.40, gain: 0.48, type: 'sine' }     // G6
    ]);
  } catch {
    // Ignore audio context restrictions
  }
}

export const LiveScorecardBanner: React.FC = () => {
  const { user, profile, isSuper } = useAuth();
  const { data: members = [] } = useMembers({ enabled: !!user });

  const isValidPhotoUrl = (url?: string | null): boolean => {
    if (!url || typeof url !== 'string') return false;
    const t = url.trim();
    if (!t || t === '?' || t === 'null' || t === 'undefined') return false;
    return t.startsWith('http://') || t.startsWith('https://') || t.startsWith('/') || t.startsWith('data:');
  };

  const resolvePlayerPhoto = (
    explicitPhoto?: string | null,
    id?: string | null,
    name?: string | null
  ) => {
    if (isValidPhotoUrl(explicitPhoto)) return explicitPhoto!;
    if (id && user?.uid === id) {
      const userPhoto = profile?.photoUrl || profile?.avatarUrl || user?.photoURL;
      if (isValidPhotoUrl(userPhoto)) return userPhoto!;
    }
    if (!id && !name) return undefined;
    const cleanName = (name || '').trim().toLowerCase();
    const isGenericName = !cleanName || cleanName === '?' || cleanName === 'partner' || cleanName === 'unassigned' || cleanName === 'opp 1' || cleanName === 'opp 2';
    const match = members.find((m) => {
      if (id && (m.id === id || (m as any).odysseyId === id || (m as any).uid === id)) return true;
      if (!isGenericName) {
        const mNick = (m.nickname || '').trim().toLowerCase();
        const mDisplay = (m.displayName || '').trim().toLowerCase();
        const mFull = `${m.firstName || ''} ${m.lastName || ''}`.trim().toLowerCase();
        const mFirst = (m.firstName || '').trim().toLowerCase();
        const mLast = (m.lastName || '').trim().toLowerCase();
        if (mNick && mNick === cleanName) return true;
        if (mDisplay && mDisplay === cleanName) return true;
        if (mFull && mFull === cleanName) return true;
        if (mFirst && mFirst === cleanName) return true;
        if (mLast && mLast === cleanName) return true;
        if (cleanName.length > 3 && (mDisplay.includes(cleanName) || cleanName.includes(mDisplay))) return true;
      }
      return false;
    });
    const photo = match?.photoUrl || match?.avatarUrl || (match as any)?.photoURL;
    return isValidPhotoUrl(photo) ? photo : undefined;
  };
  const navigate = useNavigate();
  const location = useLocation();

  const [pendingScorecards, setPendingScorecards] = useState<PendingScorecardSummary[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMinimized, setIsMinimized] = useState(false);
  const [confirmingHandIndex, setConfirmingHandIndex] = useState<number | null>(null);

  const isUserSuper = Boolean(isSuper || isSuperUser(profile));

  // Subscribe to real-time pending scorecards:
  // Super Users listen to all active club scorecards across tables;
  // Regular players listen to their personal involved match queue.
  const seenScorecardsRef = useRef<Set<string>>(new Set());
  const seenPendingVerificationRef = useRef<Set<string>>(new Set());
  const seenWaitingSubmissionRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!user?.uid) {
      setPendingScorecards([]);
      return;
    }

        const candidateIds = [
      user.uid,
      profile?.id,
      (profile as any)?.memberId,
      (profile as any)?.uid,
      (profile as any)?.odysseyId
    ].filter(Boolean) as string[];

    const candidateNames = [
      profile?.displayName,
      profile?.nickname,
      `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim(),
      user.displayName
    ].filter(Boolean) as string[];


    const updateScorecards = (list: PendingScorecardSummary[]) => {
      // Filter out matches the user created themselves UNLESS match is confirmed (waiting on your submission)
      const relevant = list.filter((s) => {
        const isCreator = candidateIds.includes(s.creatorId) || s.isUserCreator;
        if (isCreator) {
          return Boolean(s.isMatchConfirmed);
        }
        return true;
      });
      setPendingScorecards(relevant);

      let hasNewWaitingSubmission = false;
      let hasNewPendingVerification = false;
      let hasNewScorecard = false;

      for (const sc of relevant) {
        const isCreator = candidateIds.includes(sc.creatorId) || sc.isUserCreator;
        const isPending = sc.isPendingVerification ?? isScorecardPendingVerification(sc);

        // 1. Scorecard waiting on your submission (for match creator when verified)
        if (sc.isMatchConfirmed && isCreator) {
          if (!seenWaitingSubmissionRef.current.has(sc.scorecardId)) {
            seenWaitingSubmissionRef.current.add(sc.scorecardId);
            hasNewWaitingSubmission = true;
            toast.success(
              `Scorecard verified by ${sc.confirmedByMemberName || 'opponent'}! Waiting on your submission.`,
              { id: `waiting-sub-${sc.scorecardId}`, duration: 5500 }
            );
          }
        }
        // 2. Scorecard pending verification (for opponents & super users / admins)
        else if (isPending && !sc.isMatchConfirmed) {
          if (!seenPendingVerificationRef.current.has(sc.scorecardId)) {
            seenPendingVerificationRef.current.add(sc.scorecardId);
            if (sc.isUserOpponent) {
              hasNewPendingVerification = true;
              toast(
                `Scorecard ${sc.scorecardId} is pending verification! Please review and verify match scores.`,
                { icon: '⏳', id: `pending-verif-${sc.scorecardId}`, duration: 5500 }
              );
            }
          }
        }
        // 3. New live scorecard added
        else if (!seenScorecardsRef.current.has(sc.scorecardId)) {
          seenScorecardsRef.current.add(sc.scorecardId);
          hasNewScorecard = true;
        }
      }

      if (hasNewWaitingSubmission) {
        playScorecardWaitingSubmissionSound();
      } else if (hasNewPendingVerification) {
        playScorecardPendingVerificationSound();
      } else if (hasNewScorecard) {
        playScorecardAddedSound();
      }
    };

    // Scorecard notification banner must strictly only show up for participants of that match
    const unsubscribe = subscribeToUserPendingScorecards(candidateIds, candidateNames, updateScorecards);

    return () => unsubscribe();
  }, [user?.uid, profile?.id, profile?.nickname, profile?.displayName, profile?.firstName, profile?.lastName, user?.displayName]);

  // Don't render banner if there are no pending scorecards or user is not logged in
  if (!user || pendingScorecards.length === 0) {
    return null;
  }

  // Ensure index stays within bounds
  const safeIndex = Math.min(currentIndex, Math.max(0, pendingScorecards.length - 1));
  const active = pendingScorecards[safeIndex];
  const hostPhoto = resolvePlayerPhoto(active?.creatorPhotoUrl, active?.creatorId, active?.creatorName);
  const partnerPhoto = resolvePlayerPhoto(active?.partnerPhotoUrl, active?.partnerId, active?.partnerName);
  const opp1Photo = resolvePlayerPhoto(active?.opponent1PhotoUrl, active?.opponent1Id, active?.opponent1Name);
  const opp2Photo = resolvePlayerPhoto(active?.opponent2PhotoUrl, active?.opponent2Id, active?.opponent2Name);

  const isPending = active?.isPendingVerification ?? isScorecardPendingVerification(active);
  const isWaitingOnUserSubmission = Boolean(
    active?.isMatchConfirmed && (active?.isUserCreator || (user?.uid && active?.creatorId === user.uid))
  );
  const canUserVerify = Boolean(
    !active?.isMatchConfirmed && active?.isUserOpponent && !active?.isUserCreator
  );

  // Only suppress if user is already on the review screen for this specific scorecard
  const isReviewingThisScorecard = location.pathname.includes(`/scorecard/review/${active.scorecardId}`);
  if (isReviewingThisScorecard) {
    return null;
  }

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : pendingScorecards.length - 1));
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev < pendingScorecards.length - 1 ? prev + 1 : 0));
  };

  const handleQuickConfirm = async (e: React.MouseEvent, handIdx?: number, _handNum?: number) => {
    e.stopPropagation();
    const targetIdx = handIdx ?? active.unconfirmedHandIndex;
    if (targetIdx === undefined || targetIdx === null) return;

    try {
      setConfirmingHandIndex(targetIdx);
      const roleSuffix = isUserSuper && !active.isUserOpponent ? ' (Super User)' : '';
      const memberName = `${user.displayName || (active.isUserOpponent ? 'Opponent' : 'Admin')}${roleSuffix}`;

      await confirmLiveScorecardHand(
        active.scorecardId,
        targetIdx,
        user.uid,
        memberName
      );
    } catch {
      toast.error('Failed to confirm hand. Please try again.');
    } finally {
      setConfirmingHandIndex(null);
    }
  };

  // Minimized Floating Pill View
  if (isMinimized) {
    return (
      <aside
        aria-label="Live Match Quick View"
        className="fixed top-16 right-3 sm:right-6 z-[1200] animate-in fade-in slide-in-from-top-2 duration-200 drop-shadow-[0_14px_28px_rgba(0,0,0,0.9)]"
      >
        <button
          type="button"
          onClick={() => setIsMinimized(false)}
          className="flex items-center gap-2 bg-gradient-to-r from-blue-600 via-[#1d4ed8] to-sky-600 hover:from-blue-500 hover:to-sky-500 backdrop-blur-md border-2 border-sky-300/80 text-white px-3.5 py-2 rounded-2xl shadow-[0_12px_32px_rgba(0,0,0,0.95),0_0_24px_rgba(37,99,235,0.65)] hover:border-white transition-all cursor-pointer"
        >
          {active.isMatchConfirmed ? (
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-85" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500 ring-1 ring-white/80" />
            </span>
          ) : isPending ? (
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-85" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500 ring-1 ring-white/80" />
            </span>
          ) : (
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-300 opacity-85" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#c72f38] ring-1 ring-white/80" />
            </span>
          )}

          <span className="text-xs font-black uppercase tracking-wider text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
            T1 {active.team1Score} - T2 {active.team2Score}
          </span>

          {active.isMatchConfirmed ? (
            <span className="text-[10px] font-extrabold text-emerald-200 bg-emerald-950/70 px-1.5 py-0.5 rounded-full border border-emerald-400/50">
              {isWaitingOnUserSubmission ? 'Submit' : 'Verified'}
            </span>
          ) : isPending ? (
            <span className="text-[10px] font-extrabold text-amber-200 bg-amber-950/70 px-1.5 py-0.5 rounded-full border border-amber-400/50">
              {canUserVerify ? 'Verify' : 'Pending'}
            </span>
          ) : pendingScorecards.length > 1 ? (
            <span className="text-[10px] font-extrabold text-amber-200 bg-black/30 px-1.5 py-0.5 rounded-full border border-amber-300/40">
              {pendingScorecards.length} live
            </span>
          ) : null}

          <ChevronRight size={14} className="text-white/90" />
        </button>
      </aside>
    );
  }

  return (
    <aside
      aria-label="Live Match Scoring Banner"
      className="fixed top-16 left-0 right-0 z-[1200] px-3 sm:px-6 pointer-events-none flex justify-center animate-in fade-in slide-in-from-top-3 duration-200 drop-shadow-[0_24px_45px_rgba(0,0,0,0.95)]"
    >
      <div className="w-full max-w-2xl relative overflow-hidden rounded-2xl sm:rounded-3xl border-2 border-blue-500/55 ring-1 ring-sky-400/30 shadow-[0_24px_60px_rgba(0,0,0,0.95),0_10px_35px_rgba(37,99,235,0.45),0_0_40px_rgba(20,90,180,0.35)] bg-slate-950 pointer-events-auto flex flex-col p-3 sm:p-4 select-none">

        {/* DIAGONAL SPLIT VECTOR BACKGROUND WITH SPEED STREAKS & GLOWING SLASH BEAM */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none select-none"
          preserveAspectRatio="none"
          viewBox="0 0 100 100"
        >
          <defs>
            <linearGradient id="notif-blue-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#071930" />
              <stop offset="50%" stopColor="#0d2e59" />
              <stop offset="100%" stopColor="#144682" />
            </linearGradient>

            <linearGradient id="notif-red-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#520a0f" />
              <stop offset="50%" stopColor="#7a1219" />
              <stop offset="100%" stopColor="#a81924" />
            </linearGradient>

            <filter id="notif-slash-glow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="1.2" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          {/* Left: Blue Team 1 Polygon */}
          <polygon points="0,0 56,0 44,100 0,100" fill="url(#notif-blue-grad)" />

          {/* Right: Red Team 2 Polygon */}
          <polygon points="56,0 100,0 100,100 44,100" fill="url(#notif-red-grad)" />

          {/* Speed Lines: Left Blue Side */}
          <rect x="2" y="16" width="24" height="1.2" fill="#38bdf8" opacity="0.25" rx="0.6" />
          <rect x="8" y="24" width="30" height="1.8" fill="#38bdf8" opacity="0.18" rx="0.9" />
          <line x1="2" y1="40" x2="48" y2="40" stroke="#38bdf8" strokeWidth="0.8" strokeDasharray="5 3" opacity="0.45" />
          <line x1="2" y1="54" x2="48" y2="54" stroke="#38bdf8" strokeWidth="0.8" strokeDasharray="7 3" opacity="0.35" />

          {/* Speed Lines: Right Red Side */}
          <rect x="74" y="18" width="22" height="1.2" fill="#fca5a5" opacity="0.25" rx="0.6" />
          <rect x="62" y="28" width="28" height="1.8" fill="#fca5a5" opacity="0.18" rx="0.9" />
          <line x1="52" y1="40" x2="98" y2="40" stroke="#fca5a5" strokeWidth="0.8" strokeDasharray="5 3" opacity="0.45" />
          <line x1="52" y1="54" x2="98" y2="54" stroke="#fca5a5" strokeWidth="0.8" strokeDasharray="7 3" opacity="0.35" />

          {/* Electric Cyan Slash Beam */}
          <polygon points="54,0 56.5,0 44.5,100 42,100" fill="#00d2ff" opacity="0.85" filter="url(#notif-slash-glow)" />

          {/* Crisp White Core Slash Line */}
          <line x1="56" y1="0" x2="44" y2="100" stroke="#ffffff" strokeWidth="0.8" opacity="0.95" />
        </svg>

        {/* TOP META BAR: Live indicator, Match ID, Role Badge, Carousel, Minimize */}
        <div className="relative z-10 flex items-center justify-between gap-2 border-b border-white/10 pb-2 mb-2">
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            {/* Dynamic Status Indicator */}
            {active.isMatchConfirmed ? (
              <div className="flex items-center gap-1.5 bg-emerald-950/80 border border-emerald-400/60 px-2 py-0.5 rounded-full shadow-[0_0_12px_rgba(16,185,129,0.35)]">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                </span>
                <span className="text-[9px] font-black uppercase tracking-widest text-emerald-300">
                  WAITING ON SUBMISSION
                </span>
              </div>
            ) : isPending ? (
              <div className="flex items-center gap-1.5 bg-amber-950/80 border border-amber-400/60 px-2 py-0.5 rounded-full shadow-[0_0_12px_rgba(245,158,11,0.35)]">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400" />
                </span>
                <span className="text-[9px] font-black uppercase tracking-widest text-amber-300">
                  PENDING VERIFICATION
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 bg-red-950/70 border border-red-500/40 px-2 py-0.5 rounded-full">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                </span>
                <span className="text-[9px] font-black uppercase tracking-widest text-red-300">
                  LIVE SCORECARD
                </span>
              </div>
            )}

            <span className="text-xs font-black text-white/90">
              {active.scorecardId}
            </span>

            {pendingScorecards.length > 1 && (
              <div className="flex items-center gap-1 bg-white/5 border border-white/10 px-1.5 py-0.5 rounded-lg text-white/80">
                <button
                  type="button"
                  onClick={handlePrev}
                  className="p-0.5 hover:text-white transition-colors cursor-pointer border-none bg-transparent"
                  title="Previous match"
                >
                  <ChevronLeft size={12} />
                </button>
                <span className="text-[10px] font-extrabold text-amber-300 px-1">
                  {safeIndex + 1}/{pendingScorecards.length}
                </span>
                <button
                  type="button"
                  onClick={handleNext}
                  className="p-0.5 hover:text-white transition-colors cursor-pointer border-none bg-transparent"
                  title="Next match"
                >
                  <ChevronRight size={12} />
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Role Badge */}
            {active.isUserCreator ? (
              <span className="text-[9.5px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-indigo-600 text-white shadow-sm border border-indigo-400/30">
                Host
              </span>
            ) : active.isUserPartner ? (
              <span className="text-[9.5px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-sky-500 text-white shadow-sm border border-sky-300/30">
                Partner
              </span>
            ) : active.isUserOpponent ? (
              <span className="text-[9.5px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-400 text-slate-950 shadow-sm font-extrabold">
                Opponent
              </span>
            ) : isUserSuper ? (
              <span className="text-[9.5px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white flex items-center gap-1 shadow-sm">
                <Shield size={10} />
                Super User
              </span>
            ) : null}

            {/* Minimize */}
            <button
              type="button"
              onClick={() => setIsMinimized(true)}
              className="w-6 h-6 rounded-lg bg-white/10 hover:bg-white/20 text-white/60 hover:text-white flex items-center justify-center transition-colors border-none cursor-pointer"
              title="Minimize banner"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* ============================================================== */}
        {/* RUMBLE ARENA GRID: TEAM 1 | GIANT 3D VS + SCORE | TEAM 2       */}
        {/* ============================================================== */}
        <div className="relative z-10 grid grid-cols-[1fr_auto_1fr] items-center gap-1.5 sm:gap-3 py-1">

          {/* TEAM 1 (BLUE SIDE) */}
          <div className="flex items-center justify-center gap-2 sm:gap-3 min-w-0">
            {/* Creator / Host */}
            <div className="flex flex-col items-center text-center min-w-0">
              <div className="relative mb-0.5 flex items-center justify-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden shrink-0 aspect-square ring-2 ring-sky-400 ring-offset-1 ring-offset-[#071930] shadow-md shadow-sky-500/30 flex items-center justify-center bg-gradient-to-br from-sky-600 to-blue-800">
                  {hostPhoto ? (
                    <img
                      src={hostPhoto}
                      alt={active.creatorName || 'Host'}
                      className="w-full h-full object-cover block"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const fallback = e.currentTarget.parentElement?.querySelector('.avatar-initials');
                        if (fallback) fallback.classList.remove('hidden');
                      }}
                    />
                  ) : null}
                  <span className={`text-xs sm:text-sm font-black text-white avatar-initials ${hostPhoto ? 'hidden' : ''}`}>
                    {(active.creatorName && active.creatorName !== '?' ? active.creatorName : 'H')[0]?.toUpperCase()}
                  </span>
                </div>
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-1.5 py-0.2 rounded-full bg-sky-500 text-[7px] sm:text-[8px] font-black uppercase text-white shadow whitespace-nowrap z-10">
                  Host
                </span>
              </div>
              <span className="text-[10px] sm:text-xs font-black text-white truncate max-w-[50px] sm:max-w-[70px] mt-0.5">
                {active.creatorName && active.creatorName !== '?' ? active.creatorName : 'Host'}
              </span>
            </div>

            {/* Partner */}
            <div className="flex flex-col items-center text-center min-w-0">
              <div className="relative mb-0.5 flex items-center justify-center">
                {active.partnerId || active.partnerName ? (
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden shrink-0 aspect-square ring-2 ring-sky-400 ring-offset-1 ring-offset-[#071930] shadow-md shadow-sky-500/30 flex items-center justify-center bg-gradient-to-br from-sky-600 to-blue-800">
                    {partnerPhoto ? (
                      <img
                        src={partnerPhoto}
                        alt={active.partnerName || 'Partner'}
                        className="w-full h-full object-cover block"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.parentElement?.querySelector('.avatar-initials');
                          if (fallback) fallback.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <span className={`text-xs sm:text-sm font-black text-white avatar-initials ${partnerPhoto ? 'hidden' : ''}`}>
                      {(active.partnerName && active.partnerName !== '?' ? active.partnerName : 'P')[0]?.toUpperCase()}
                    </span>
                  </div>
                ) : (
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-sky-950/60 border border-sky-400/40 flex items-center justify-center text-[9px] font-bold text-sky-300">
                    Slot
                  </div>
                )}
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-1.5 py-0.2 rounded-full bg-sky-600 text-[7px] sm:text-[8px] font-black uppercase text-white shadow whitespace-nowrap z-10">
                  Partner
                </span>
              </div>
              <span className="text-[10px] sm:text-xs font-black text-white truncate max-w-[50px] sm:max-w-[70px] mt-0.5">
                {active.partnerName && active.partnerName !== '?' ? active.partnerName : 'Partner'}
              </span>
            </div>
          </div>
          {/* CENTER: 3D VS & RUNNING SCORE (MUCH BIGGER SCORE) */}
          <div className="flex flex-col items-center justify-center shrink-0 px-1 sm:px-3">
            <span className="font-black italic tracking-tighter text-2xl sm:text-3xl text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.9)] select-none">
              VS
            </span>
            <div className="mt-1 flex items-center gap-1.5 sm:gap-2 px-3 py-1 bg-black/80 backdrop-blur-md rounded-xl border border-white/25 shadow-xl">
              <span className="text-lg sm:text-2xl font-black text-sky-400 tracking-tight">
                {active.team1Score}
              </span>
              <span className="text-white/40 text-sm font-bold">:</span>
              <span className="text-lg sm:text-2xl font-black text-rose-400 tracking-tight">
                {active.team2Score}
              </span>
            </div>
          </div>

          {/* TEAM 2 (RED SIDE) */}
          <div className="flex items-center justify-center gap-2 sm:gap-3 min-w-0">
            {/* Opponent 1 */}
            <div className="flex flex-col items-center text-center min-w-0">
              <div className="relative mb-0.5 flex items-center justify-center">
                {active.opponent1Id || active.opponent1Name ? (
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden shrink-0 aspect-square ring-2 ring-rose-500 ring-offset-1 ring-offset-[#520a0f] shadow-md shadow-rose-500/30 flex items-center justify-center bg-gradient-to-br from-rose-700 to-red-900">
                    {opp1Photo ? (
                      <img
                        src={opp1Photo}
                        alt={active.opponent1Name || 'Opp 1'}
                        className="w-full h-full object-cover block"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.parentElement?.querySelector('.avatar-initials');
                          if (fallback) fallback.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <span className={`text-xs sm:text-sm font-black text-white avatar-initials ${opp1Photo ? 'hidden' : ''}`}>
                      {(active.opponent1Name && active.opponent1Name !== '?' ? active.opponent1Name : 'O')[0]?.toUpperCase()}
                    </span>
                  </div>
                ) : (
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-rose-950/60 border border-rose-400/40 flex items-center justify-center text-[9px] font-bold text-rose-300">
                    Slot
                  </div>
                )}
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-1.5 py-0.2 rounded-full bg-rose-600 text-[7px] sm:text-[8px] font-black uppercase text-white shadow whitespace-nowrap z-10">
                  Opp 1
                </span>
              </div>
              <span className="text-[10px] sm:text-xs font-black text-white truncate max-w-[50px] sm:max-w-[70px] mt-0.5">
                {active.opponent1Name || 'Opp 1'}
              </span>
            </div>

            {/* Opponent 2 */}
            <div className="flex flex-col items-center text-center min-w-0">
              <div className="relative mb-0.5 flex items-center justify-center">
                {active.opponent2Id || active.opponent2Name ? (
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden shrink-0 aspect-square ring-2 ring-rose-500 ring-offset-1 ring-offset-[#520a0f] shadow-md shadow-rose-500/30 flex items-center justify-center bg-gradient-to-br from-rose-700 to-red-900">
                    {opp2Photo ? (
                      <img
                        src={opp2Photo}
                        alt={active.opponent2Name || 'Opp 2'}
                        className="w-full h-full object-cover block"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.parentElement?.querySelector('.avatar-initials');
                          if (fallback) fallback.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <span className={`text-xs sm:text-sm font-black text-white avatar-initials ${opp2Photo ? 'hidden' : ''}`}>
                      {(active.opponent2Name && active.opponent2Name !== '?' ? active.opponent2Name : 'O')[0]?.toUpperCase()}
                    </span>
                  </div>
                ) : (
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-rose-950/60 border border-rose-400/40 flex items-center justify-center text-[9px] font-bold text-rose-300">
                    Slot
                  </div>
                )}
                <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-1.5 py-0.2 rounded-full bg-rose-700 text-[7px] sm:text-[8px] font-black uppercase text-white shadow whitespace-nowrap z-10">
                  Opp 2
                </span>
              </div>
              <span className="text-[10px] sm:text-xs font-black text-white truncate max-w-[50px] sm:max-w-[70px] mt-0.5">
                {active.opponent2Name || 'Opp 2'}
              </span>
            </div>
          </div>

        </div>

        {/* BOTTOM ACTION BAR: Dynamic Contextual Status & Direct Action Button */}
        <div className="relative z-10 flex items-center justify-between gap-2 pt-2 mt-2 border-t border-white/10">
          <div className="flex items-center gap-1.5 min-w-0">
            {active.isMatchConfirmed ? (
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-[9px] font-extrabold text-emerald-400 bg-emerald-500/15 border border-emerald-500/30 px-2 py-0.5 rounded-full shrink-0 flex items-center gap-1">
                  <Check size={10} strokeWidth={3} />
                  Verified
                </span>
                <span className="text-[11px] font-black text-emerald-300 truncate">
                  {isWaitingOnUserSubmission
                    ? 'Scorecard verified • Ready for your submission'
                    : `Scorecard verified • Waiting on submission by ${active.creatorName}`}
                </span>
              </div>
            ) : isPending ? (
              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-[9px] font-extrabold text-amber-300 bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 rounded-full shrink-0 flex items-center gap-1">
                  <Clock size={10} strokeWidth={2.5} />
                  Pending
                </span>
                <span className="text-[11px] font-bold text-amber-200 truncate">
                  {canUserVerify
                    ? 'Action Required • Opponent verification needed'
                    : 'Pending verification by opponent or admin'}
                </span>
              </div>
            ) : (
              <span className="text-[11px] font-bold text-white/80 truncate">
                {active.latestHandSummary || 'Match in progress'}
              </span>
            )}
          </div>

          {isWaitingOnUserSubmission ? (
            <button
              type="button"
              onClick={() => navigate(`/members/scorecard/review/${active.scorecardId}`)}
              className="py-1.5 px-3.5 bg-gradient-to-r from-emerald-600 via-teal-600 to-emerald-700 hover:from-emerald-500 hover:to-teal-500 text-white font-black text-xs rounded-xl shadow-lg shadow-emerald-600/35 border border-emerald-400/40 transition-all active:scale-95 cursor-pointer flex items-center gap-1.5 shrink-0"
            >
              <Save size={13} />
              <span>Submit Scorecard</span>
            </button>
          ) : canUserVerify && isPending ? (
            <button
              type="button"
              onClick={() => navigate(`/members/scorecard/review/${active.scorecardId}`)}
              className="py-1.5 px-3.5 bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-black text-xs rounded-xl shadow-lg shadow-amber-500/35 border border-amber-300/50 transition-all active:scale-95 cursor-pointer flex items-center gap-1.5 shrink-0"
            >
              <Check size={13} strokeWidth={3} />
              <span>Verify Scorecard</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={() => navigate(`/members/scorecard/review/${active.scorecardId}`)}
              className="py-1.5 px-3.5 bg-gradient-to-r from-sky-600 via-blue-600 to-indigo-700 hover:from-sky-500 hover:to-blue-500 text-white font-black text-xs rounded-xl shadow-lg shadow-sky-600/30 border border-sky-400/40 transition-all active:scale-95 cursor-pointer flex items-center gap-1.5 shrink-0"
            >
              <Eye size={13} />
              <span>Review Scorecard</span>
            </button>
          )}
        </div>

      </div>
    </aside>
  );
};
